import { forwardRef, useEffect, useRef, useState, type ChangeEvent, type ReactNode, type ButtonHTMLAttributes, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { FolderTree, Info, Plus } from "lucide-react";
import { useToast } from "../stores/toast";

// ---------- Form field chrome (aligned rows) ----------

function FieldLabel({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <span className="mb-1 block min-h-4 whitespace-nowrap text-xs font-medium leading-4 text-muted">{children}</span>
  );
}

function FieldHint({ children }: { children?: ReactNode }) {
  return (
    <span
      className={`mt-1 block min-h-[14px] text-[10px] leading-[14px] ${children ? "text-muted-2" : "invisible"}`}
      aria-hidden={!children}
    >
      {children ?? "\u00a0"}
    </span>
  );
}

const fieldInputClass =
  "w-full rounded-xl border border-line bg-input-bg px-3 py-2 text-base text-foreground placeholder:text-muted-2 outline-none transition-colors focus:border-brand-pink/60 lg:text-sm";

// ---------- Primitives ----------

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <div className={`h-5 w-5 animate-spin rounded-full border-2 border-brand-pink border-t-transparent ${className}`} />
  );
}

export function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

export function EmptyState({
  icon: Icon = FolderTree,
  title,
  hint,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center">
      <Icon size={48} strokeWidth={1.25} className="text-muted-2" />
      <p className="font-display text-sm text-foreground">{title}</p>
      {hint && <p className="max-w-72 text-xs text-muted">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger" | "outline";
  size?: "sm" | "md";
}) {
  const styles = {
    primary: "bg-gradient-brand text-white font-semibold shadow-md shadow-brand-pink/20 hover:brightness-110",
    ghost: "bg-input-bg text-foreground hover:bg-hover",
    outline: "border border-brand-pink/40 text-brand-pink hover:bg-brand-pink/10",
    danger: "bg-red-500/85 text-white font-semibold hover:bg-red-500",
  };
  const sizes = { sm: "px-2.5 py-1.5 text-xs rounded-lg", md: "px-4 py-2 text-sm rounded-xl" };
  return (
    <button
      className={`inline-flex flex-row items-center justify-center gap-1.5 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 ${styles[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, "");
}

export type NumericFilterOptions = {
  decimal?: boolean;
  allowNegative?: boolean;
};

/** Strip invalid characters while typing — keeps empty string (no snap to 0). */
export function filterNumericInput(raw: string, opts: NumericFilterOptions = {}): string {
  const { decimal = false, allowNegative = false } = opts;
  let out = "";
  let dotUsed = false;
  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") {
      out += ch;
      continue;
    }
    if (decimal && ch === "." && !dotUsed) {
      dotUsed = true;
      out += ch;
      continue;
    }
    if (allowNegative && ch === "-" && out.length === 0) {
      out += ch;
    }
  }
  return out;
}

function numericInputAllowsDecimal(step?: string | number, inputMode?: HTMLInputElement["inputMode"]): boolean {
  if (inputMode === "decimal") return true;
  if (step === undefined || step === "any") return true;
  return String(step).includes(".");
}

function numericInputAllowsNegative(min?: string | number): boolean {
  if (min === undefined || min === "") return false;
  const n = Number(min);
  return Number.isFinite(n) && n < 0;
}

export function numToField(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return String(value);
}

export function parseIntField(raw: string, fallback = 0): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

export function parseDecimalField(raw: string, fallback = 0): number {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function parsePositiveInt(raw: string, fallback = 1): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Text-based numeric input — allows clearing the field while editing (no snap to 0). */
export const NumberInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> & {
    label?: string;
    hint?: string;
    value: string;
    onValueChange: (value: string) => void;
    decimal?: boolean;
    allowNegative?: boolean;
    /** Applied on blur when the field is empty. Omit to leave empty. */
    blurFallback?: string;
  }
>(function NumberInput(
  {
    label,
    hint,
    className = "",
    value,
    onValueChange,
    onBlur,
    decimal = false,
    allowNegative = false,
    blurFallback,
    inputMode,
    ...props
  },
  ref,
) {
  return (
    <Input
      ref={ref}
      label={label}
      hint={hint}
      type="number"
      inputMode={inputMode ?? (decimal ? "decimal" : "numeric")}
      decimal={decimal}
      allowNegative={allowNegative}
      className={className}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onBlur={(e) => {
        if (blurFallback !== undefined && value === "") onValueChange(blurFallback);
        onBlur?.(e);
      }}
      {...props}
    />
  );
});

/** Numeric input backed by a number in parent state — clearable while editing; reverts on blur if left empty. */
export function NumberField({
  label,
  hint,
  className = "",
  value,
  onChange,
  decimal = false,
  allowNegative = false,
  min,
  max,
  step,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> & {
  label?: string;
  hint?: string;
  value: number;
  onChange: (value: number) => void;
  decimal?: boolean;
  allowNegative?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? numToField(value);

  return (
    <Input
      label={label}
      hint={hint}
      type="number"
      className={className}
      decimal={decimal}
      allowNegative={allowNegative}
      min={min}
      max={max}
      step={step}
      value={shown}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft === null) return;
        const raw = draft.trim();
        setDraft(null);
        if (raw === "") return;
        let next = decimal ? parseDecimalField(raw, value) : parseIntField(raw, value);
        if (min !== undefined && next < Number(min)) next = Number(min);
        if (max !== undefined && next > Number(max)) next = Number(max);
        onChange(next);
      }}
      {...props}
    />
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & {
    label?: string;
    hint?: string;
    /** When type="number", allow decimal point (auto-detected from step unless set). */
    decimal?: boolean;
    /** When type="number", allow leading minus (auto-detected from min unless set). */
    allowNegative?: boolean;
  }
>(function Input(
  { label, hint, className = "", type, value, onChange, onBlur, min, max, step, inputMode, decimal, allowNegative, ...props },
  ref,
) {
  if (type === "number") {
    const useDecimal = decimal ?? numericInputAllowsDecimal(step, inputMode);
    const useNegative = allowNegative ?? numericInputAllowsNegative(min);
    const display =
      value === null || value === undefined ? "" : typeof value === "number" ? numToField(value) : String(value);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const filtered = filterNumericInput(e.target.value, { decimal: useDecimal, allowNegative: useNegative });
      onChange?.({
        ...e,
        target: { ...e.target, value: filtered },
        currentTarget: { ...e.currentTarget, value: filtered },
      } as ChangeEvent<HTMLInputElement>);
    };

    return (
      <label className="block">
        <FieldLabel>{label}</FieldLabel>
        <input
          ref={ref}
          type="text"
          inputMode={inputMode ?? (useDecimal ? "decimal" : "numeric")}
          className={`${fieldInputClass} ${className}`}
          value={display}
          onChange={handleChange}
          onBlur={onBlur}
          {...props}
        />
        <FieldHint>{hint}</FieldHint>
      </label>
    );
  }

  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input ref={ref} className={`${fieldInputClass} ${className}`} type={type} value={value} onChange={onChange} onBlur={onBlur} min={min} max={max} step={step} inputMode={inputMode} {...props} />
      <FieldHint>{hint}</FieldHint>
    </label>
  );
});

export function Select({
  label,
  hint,
  className = "",
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <select
        className={`w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-brand-pink/60 ${className}`}
        {...props}
      >
        {children}
      </select>
      <FieldHint>{hint}</FieldHint>
    </label>
  );
}

export function Textarea({
  label,
  hint,
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <textarea
        className={`${fieldInputClass} ${className}`}
        {...props}
      />
      <FieldHint>{hint}</FieldHint>
    </label>
  );
}

export function ColorField({
  label,
  hint,
  value,
  onChange,
  className = "",
}: {
  label?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  const normalized = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#5460ed";

  function handleHexInput(raw: string) {
    const next = raw.startsWith("#") ? raw : `#${raw}`;
    if (/^#[0-9a-fA-F]{0,6}$/.test(next)) onChange(next.length === 7 ? next.toLowerCase() : next);
  }

  return (
    <label className={`block ${className}`}>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex h-[38px] items-center gap-2 rounded-xl border border-line bg-input-bg px-3 text-sm focus-within:border-brand-pink/60">
        <input
          type="color"
          value={normalized}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          className="color-field-swatch h-5 w-7 shrink-0 cursor-pointer rounded-md border-0 bg-transparent p-0"
          aria-label={label ? `${label} picker` : "Color picker"}
        />
        <input
          type="text"
          value={normalized}
          onChange={(e) => handleHexInput(e.target.value.trim())}
          className="min-w-0 flex-1 bg-transparent font-mono text-sm uppercase text-foreground outline-none placeholder:text-muted-2"
          placeholder="#5460ED"
          maxLength={7}
          spellCheck={false}
        />
      </div>
      <FieldHint>{hint}</FieldHint>
    </label>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 disabled:opacity-40"
    >
      <span
        className={`relative inline-flex h-5.5 w-10 shrink-0 items-center rounded-full p-0.5 transition-colors ${
          checked ? "bg-gradient-brand" : "bg-surface-3"
        }`}
        style={{ height: 22 }}
      >
        <span
          className={`h-4.5 w-4.5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`}
          style={{ height: 18, width: 18 }}
        />
      </span>
      {label && <span className="text-sm text-foreground">{label}</span>}
    </button>
  );
}

export type BadgeColor = "pink" | "blue" | "green" | "amber" | "gray" | "red";

export function Badge({ children, color = "pink" }: { children: ReactNode; color?: BadgeColor }) {
  const colors: Record<BadgeColor, string> = {
    pink: "bg-brand-pink/15 text-brand-pink",
    blue: "bg-brand-blue/20 text-indigo-300",
    green: "bg-emerald-500/15 text-emerald-300",
    amber: "bg-amber-500/15 text-amber-300",
    gray: "bg-hover text-muted",
    red: "bg-red-500/15 text-red-300",
  };
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colors[color]}`}>
      {children}
    </span>
  );
}

export function statusColor(status: string): BadgeColor {
  switch (status) {
    case "OPEN":
    case "LIVE":
    case "PAID":
    case "DELIVERED":
    case "COMPLETED":
    case "VERIFIED":
    case "PUBLISHED":
    case "IDENTIFIED":
    case "RESOLVED":
      return "green";
    case "PENDING_PAYMENT":
    case "SCHEDULED":
    case "PENDING":
    case "IN_PROGRESS":
    case "PROCESSING":
    case "QUEUED":
    case "IDENTIFYING":
      return "amber";
    case "SHIPPED":
    case "PICKED":
    case "PACKED":
    case "DRAWN":
    case "ENDED":
    case "REVIEW":
      return "blue";
    case "CANCELLED":
    case "FORFEITED":
    case "REJECTED":
    case "FAILED":
      return "red";
    case "DUPLICATE_SKIPPED":
      return "gray";
    case "APPROVED":
      return "green";
    default:
      return "gray";
  }
}

// ---------- Pill segment switcher ----------

export function PillSegmentSwitcher<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; badge?: number }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex gap-1 rounded-full bg-surface p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`relative rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
            value === opt.value ? "bg-gradient-brand text-white" : "text-muted hover:text-foreground"
          }`}
        >
          {opt.label}
          {opt.badge != null && opt.badge > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-pink px-1 text-[9px] font-bold text-white">
              {opt.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---------- FAB ----------

export function Fab({ onClick, label = "New" }: { onClick: () => void; label?: string }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-gradient-brand px-5 py-3 text-sm font-bold text-white shadow-2xl shadow-brand-pink/30 transition-transform hover:scale-105 active:scale-95"
    >
      <Plus size={16} aria-hidden /> {label}
    </button>
  );
}

// ---------- Settings card ----------

export function SettingsCard({
  title,
  description,
  headerActions,
  children,
  footer,
}: {
  title: string;
  description?: string;
  headerActions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-line bg-surface">
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h3 className="font-display text-sm font-semibold text-foreground">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
        </div>
        {headerActions ? <div className="shrink-0">{headerActions}</div> : null}
      </div>
      <div className="sidebar-form px-5 py-4">{children}</div>
      {footer && <div className="border-t border-line bg-hover px-5 py-3">{footer}</div>}
    </section>
  );
}

// ---------- Modal ----------

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = "md",
}: Readonly<{
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}>) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const maxWidth =
    size === "lg" ? "max-w-5xl" : size === "sm" ? "max-w-sm" : "max-w-lg";

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[min(90vh,calc(100vh-2rem))] w-full flex-col overflow-hidden rounded-2xl border border-line bg-surface-2 shadow-2xl ${maxWidth}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id="modal-title" className="font-display truncate text-sm font-semibold">
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 text-xs text-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-input-bg text-muted transition-colors hover:bg-hover hover:text-foreground"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="shrink-0 border-t border-line bg-hover px-5 py-3">{footer}</footer>
        )}
      </div>
    </div>
  );
}

// ---------- Confirm dialog ----------

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  danger,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-sm rounded-2xl border border-line bg-surface-2 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-sm font-semibold">{title}</h3>
        {message && <p className="mt-2 text-xs leading-relaxed text-muted">{message}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} size="sm" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Small helper hook for confirm-then-act flows. */
export function useConfirm() {
  const [state, setState] = useState<{
    open: boolean;
    title: string;
    message?: string;
    confirmLabel?: string;
    danger?: boolean;
    action: () => unknown;
  }>({ open: false, title: "", action: () => {} });

  const confirm = (opts: Omit<typeof state, "open">) => setState({ ...opts, open: true });
  const dialog = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      danger={state.danger}
      onConfirm={() => {
        setState((s) => ({ ...s, open: false }));
        void Promise.resolve(state.action());
      }}
      onCancel={() => setState((s) => ({ ...s, open: false }))}
    />
  );
  return { confirm, dialog };
}

// ---------- Toasts ----------

export function Toasts() {
  const { toasts, dismiss } = useToast();
  return (
    <div className="pointer-events-none fixed top-4 left-1/2 z-[99] flex w-full max-w-md -translate-x-1/2 flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium shadow-xl backdrop-blur-md ${
            t.kind === "success"
              ? "bg-emerald-500/90 text-white"
              : t.kind === "error"
                ? "bg-red-500/90 text-white"
                : "border border-line bg-surface-2/95 text-foreground"
          }`}
        >
          <span className="min-w-0 flex-1">{t.message}</span>
          {t.action && (
            <button
              type="button"
              onClick={t.action.onClick}
              className="shrink-0 rounded-lg bg-white/20 px-2.5 py-1 text-xs font-semibold hover:bg-white/30"
            >
              {t.action.label}
            </button>
          )}
          {!t.action && (
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="shrink-0 text-muted hover:text-foreground"
              aria-label="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function InfoTip({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl border border-line bg-input-bg text-muted transition-colors hover:bg-hover hover:text-foreground"
      >
        <Info size={16} strokeWidth={2} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-50 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface p-3 shadow-xl shadow-black/30"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export { SearchSelect, type SearchSelectOption, type SearchSelectProps } from "./SearchSelect";
